/**
 * Welcome / Purchase Crypto page. Rendered by both /welcome (no auth) and /purchase-crypto (email-protected).
 * Same UI and flow; /purchase-crypto is wrapped in EmailAuthProtectedRoute in App.tsx.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCardContext } from '../contexts/CardContext';
import { ApiService, BanxaBulkPricesResponse, BanxaQuote, BanxaPrice, BanxaPaymentMethod } from '../services/apiService';
import { CardState } from '../types/cardTypes';
import { saveQuote } from '../utils/quoteStorage';
import {
  getBanxaPaymentMethodDisplay,
  pickDefaultPaymentMethodForAmount,
} from '../utils/banxaPaymentMethodDisplay';
// @mcc-dev-preview:import
import { resolveDevState } from '../dev/devPreview';
// @mcc-dev-preview:import
import {
  DEV_PREVIEW_BANXA_PRICES,
  DEV_PREVIEW_BANXA_PRICES_FOUR_PM,
  DEV_PREVIEW_CRYPTO_PRODUCTS,
} from '../dev/devPreviewMocks';
import './PurchaseCrypto.css';
import stepImg from "./steps-banner.png";

// @mcc-dev-preview:start dev-preview-types
type PurchaseCryptoDevState = 'loading' | 'ready' | 'four-payment-methods' | 'no-coin';

const PURCHASE_CRYPTO_DEV_STATES: PurchaseCryptoDevState[] = [
  'loading',
  'ready',
  'four-payment-methods',
  'no-coin',
];
// @mcc-dev-preview:end dev-preview-types

interface CryptoProduct {
  id: string;
  name: string;
  symbol: string;
  purchaseOptions: number[];
  decimalPlaces: number;
  description?: string;
}

interface PurchaseCalculation {
  product: CryptoProduct;
  quote: BanxaQuote;
  selectedPrice: BanxaPrice;
  amount: number;
  cryptoAmount: number;
  feeAmount: number;
  networkFee: number;
  totalCost: number;
}

const PurchaseCrypto: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const devState = resolveDevState(searchParams, PURCHASE_CRYPTO_DEV_STATES, [
    {
      when: (p) => p.get('devState') === 'purchase-loading',
      state: 'loading',
    },
    {
      when: (p) => p.get('devState') === 'purchase-ready',
      state: 'ready',
    },
    {
      when: (p) => p.get('fourPaymentMethods') === '1' || p.get('fourPaymentMethods') === 'true',
      state: 'four-payment-methods',
    },
  ]);
  const cardId = searchParams.get('cardId');
  const { cardData, fetchCardData, isLoading: isCardLoading, cardId: contextCardId } = useCardContext();
  // Coin is determined by the card - no selection needed
  const cardCoinSymbol = cardData?.coinSymbol || null;
  const effectiveCoinSymbol =
    devState === 'no-coin' ? null : devState ? 'BTC' : cardCoinSymbol;
  const [amount, setAmount] = useState<string>('100');
  const [products, setProducts] = useState<CryptoProduct[]>([]);
  const [banxaPrices, setBanxaPrices] = useState<BanxaBulkPricesResponse | null>(null);
  const [purchaseCalculation, setPurchaseCalculation] = useState<PurchaseCalculation | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<BanxaPaymentMethod | null>(null);
  const [showFullSummary, setShowFullSummary] = useState<boolean>(false);

  // Calculate purchase using Banxa data
  const calculatePurchaseWithBanxa = useCallback((symbol: string, amount: number, banxaData: BanxaBulkPricesResponse, productsData: CryptoProduct[], paymentMethod?: BanxaPaymentMethod) => {
    const product = productsData.find(p => p.symbol === symbol);
    if (!product) {
      console.warn(`Product not found for symbol: ${symbol}`);
      setPurchaseCalculation(null);
      return;
    }
    
    if (!banxaData || !banxaData.success) {
      console.warn('Banxa data is missing or unsuccessful:', banxaData);
      setPurchaseCalculation(null);
      return;
    }

    if (!banxaData.data || !banxaData.data.quotes) {
      console.warn('Banxa data missing quotes:', banxaData);
      setPurchaseCalculation(null);
      return;
    }

    // Find the quote for the selected amount (handle both number and string comparison, and floating point precision)
    const quote = banxaData.data.quotes.find(q => {
      const qAmount = typeof q.amount === 'string' ? parseFloat(q.amount) : q.amount;
      return Math.abs(qAmount - amount) < 0.01; // Allow small floating point differences
    });
    if (!quote) {
      console.warn(`No quote found for amount: ${amount}`);
      setPurchaseCalculation(null);
      return;
    }
    
    if (!quote.prices || quote.prices.length === 0) {
      console.warn(`Quote found but no prices available for amount: ${amount}`);
      setPurchaseCalculation(null);
      return;
    }

    // Use the selected payment method or default to first one
    const targetPaymentMethod = paymentMethod || selectedPaymentMethod;
    let selectedPrice: BanxaPrice;
    
    if (targetPaymentMethod) {
      // Find price for the selected payment method
      selectedPrice = quote.prices.find(p => p.paymentMethodId === targetPaymentMethod.id) || quote.prices[0];
    } else {
      // Use the first payment method as default
      selectedPrice = quote.prices[0];
    }
    
    const cryptoAmount = selectedPrice.cryptoAmount;
    const feeAmount = selectedPrice.processingFee;
    const networkFee = selectedPrice.networkFee;
    const totalCost = amount;
    
    const calculation: PurchaseCalculation = {
      product,
      quote,
      selectedPrice,
      amount,
      cryptoAmount,
      feeAmount,
      networkFee,
      totalCost
    };
    
    setPurchaseCalculation(calculation);
  }, [selectedPaymentMethod]);

  // Fetch Banxa prices for the card
  const fetchBanxaPrices = useCallback(async (cardId: string) => {
    try {
      if (!cardId) {
        console.error('Card ID is required to fetch prices');
        return;
      }

      const banxaResponse = await ApiService.getCardPrices(cardId, true);
      
      if (!banxaResponse.success) {
        console.error('API returned unsuccessful response:', banxaResponse);
        return;
      }
      
      if (!banxaResponse.data) {
        console.error('API response missing data property');
        return;
      }
      
      setBanxaPrices(banxaResponse);

      // Default to the payment method with lowest total fees for the initial amount ($100)
      if (banxaResponse.success && banxaResponse.data.paymentMethods.length > 0) {
        const initialAmount = 100;
        const quoteForDefault = banxaResponse.data.quotes.find((q) => {
          const qAmount = typeof q.amount === 'string' ? parseFloat(q.amount) : q.amount;
          return Math.abs(qAmount - initialAmount) < 0.01;
        });
        const picked = pickDefaultPaymentMethodForAmount(
          banxaResponse.data.paymentMethods,
          quoteForDefault?.prices,
          true
        );
        if (picked) {
          setSelectedPaymentMethod(picked);
        }
      } else {
        console.warn('No payment methods found in response');
      }
    } catch (error) {
      console.error('Error fetching card prices:', error);
      // Set banxaPrices to null on error so UI can handle it
      setBanxaPrices(null);
    }
  }, []);

  // @mcc-dev-preview:start dev-preview-seed
  useEffect(() => {
    if (!devState) return;

    if (devState === 'loading') {
      setIsLoading(true);
      return;
    }

    if (devState === 'no-coin') {
      setProducts([]);
      setBanxaPrices(null);
      setPurchaseCalculation(null);
      setSelectedPaymentMethod(null);
      setIsLoading(false);
      return;
    }

    const pricesPayload =
      devState === 'four-payment-methods'
        ? DEV_PREVIEW_BANXA_PRICES_FOUR_PM
        : DEV_PREVIEW_BANXA_PRICES;

    setProducts(DEV_PREVIEW_CRYPTO_PRODUCTS);
    setBanxaPrices(pricesPayload as BanxaBulkPricesResponse);

    const firstMethod = pricesPayload.data.paymentMethods[0] ?? null;
    setSelectedPaymentMethod(firstMethod);
    setIsLoading(false);
  }, [devState]);
  // @mcc-dev-preview:end dev-preview-seed

  // Fetch card data if not already loaded
  useEffect(() => {
    if (devState) return;

    if (cardId && !cardData && !isCardLoading) {
      fetchCardData(cardId);
    }
  }, [cardId, cardData, isCardLoading, fetchCardData, devState]);

  useEffect(() => {
    if (devState) return;

    const fetchData = async () => {
      try {
        setIsLoading(true);
        
        // Fetch products (still needed for display and amount options)
        const productsData = await ApiService.getCryptoProducts();
        
        setProducts(productsData);
        
        // Fetch Banxa prices for the card (requires cardId)
        if (cardId) {
          await fetchBanxaPrices(cardId);
        } else {
          console.warn('Card ID is missing, cannot fetch prices');
        }
        
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    // Only fetch if we have cardId and card is not loading
    if (cardId && !isCardLoading) {
      fetchData();
    } else if (!isCardLoading && cardData && !cardCoinSymbol) {
      // Card data loaded but no coin symbol - stop loading
      setIsLoading(false);
    }
  }, [cardId, fetchBanxaPrices, isCardLoading, cardData, cardCoinSymbol, devState]);

  // Calculate purchase when relevant data changes
  useEffect(() => {
    if (effectiveCoinSymbol && banxaPrices && banxaPrices.success && products.length > 0 && amount) {
      const amountNum = parseFloat(amount);
      if (!isNaN(amountNum) && amountNum > 0) {
        calculatePurchaseWithBanxa(effectiveCoinSymbol, amountNum, banxaPrices, products, selectedPaymentMethod || undefined);
      } else {
        console.warn(`Invalid amount: ${amount}`);
        setPurchaseCalculation(null);
      }
    }
  }, [effectiveCoinSymbol, banxaPrices, products, amount, selectedPaymentMethod, calculatePurchaseWithBanxa]);

  const currentAmountNum = parseFloat(amount) || 0;
  const quoteForCurrentAmount =
    banxaPrices?.success && banxaPrices.data?.quotes
      ? banxaPrices.data.quotes.find((q) => {
          const qAmount = typeof q.amount === 'string' ? parseFloat(q.amount) : q.amount;
          return Math.abs(qAmount - currentAmountNum) < 0.01;
        })
      : undefined;

  const handleAmountChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newAmount = e.target.value;
    setAmount(newAmount);
    
    if (effectiveCoinSymbol && banxaPrices && products.length > 0) {
      calculatePurchaseWithBanxa(effectiveCoinSymbol, parseInt(newAmount), banxaPrices, products, selectedPaymentMethod || undefined);
    }
  };

  const getPaymentMethodFeesTotal = (methodId: string): number | null => {
    const priceRow = quoteForCurrentAmount?.prices?.find((p) => p.paymentMethodId === methodId);
    if (priceRow == null) return null;
    return (priceRow.processingFee ?? 0) + (priceRow.networkFee ?? 0);
  };

  const formatPaymentMethodOptionLabel = (method: BanxaPaymentMethod): string => {
    const { label } = getBanxaPaymentMethodDisplay(method);
    const feesTotal = getPaymentMethodFeesTotal(method.id);
    if (feesTotal == null) {
      return label;
    }
    return `${label} · $${feesTotal.toFixed(2)} fees`;
  };

  const handlePaymentMethodChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const methodId = e.target.value;
    const paymentMethod = banxaPrices?.data.paymentMethods.find((m) => m.id === methodId);
    if (!paymentMethod) return;
    setSelectedPaymentMethod(paymentMethod);
    if (effectiveCoinSymbol && banxaPrices && products.length > 0) {
      calculatePurchaseWithBanxa(effectiveCoinSymbol, parseInt(amount), banxaPrices, products, paymentMethod);
    }
  };

  const toggleFullSummary = () => {
    setShowFullSummary(!showFullSummary);
  };

  const handlePurchase = async () => {
    if (devState) return;

    const finalCardId = contextCardId || cardId;

    if (finalCardId && purchaseCalculation) {
      try {
        const quoteData = {
          fiatAmount: purchaseCalculation.amount,
          cryptoAmount: purchaseCalculation.cryptoAmount,
          fiatCode: purchaseCalculation.selectedPrice.fiatCode || 'AUD',
          coinCode: purchaseCalculation.selectedPrice.coinCode || cardCoinSymbol || '',
          paymentMethodId: purchaseCalculation.selectedPrice.paymentMethodId,
          processingFee: purchaseCalculation.feeAmount,
          networkFee: purchaseCalculation.networkFee,
          blockchain: purchaseCalculation.selectedPrice.blockchain
        };
        saveQuote(finalCardId, quoteData);
      } catch (error) {
        console.error('Error saving quote to localStorage:', error);
      }
    }

    if (finalCardId) {
      if (cardData?.state === CardState.READY_FOR_PURCHASE) {
        navigate(`/redirect-to-banxa?cardId=${finalCardId}`);
      } else {
        navigate(`/new-customer?cardId=${finalCardId}`);
      }
    } else {
      navigate('/new-customer');
    }
  };

  const selectedProduct = effectiveCoinSymbol ? products.find(product => product.symbol === effectiveCoinSymbol) : null;
  
  // Get amount options from the card's coin product, or default to $100 if no product selected
  const amountOptions = selectedProduct?.purchaseOptions || [100];
  
  // Get the coin name for display
  const coinName = selectedProduct ? `${selectedProduct.name} (${selectedProduct.symbol})` : cardData?.coin || effectiveCoinSymbol || 'Unknown';

  // Show loading while card data is being fetched or while fetching product data
  if (devState === 'loading' || isLoading || (!devState && isCardLoading)) {
    return (
      <div className="purchase-crypto-container ev-page ev-page-enter">
        <div className="loading-card">
          <div className="loading-spinner"></div>
          {isCardLoading ? 'Loading card information...' : 'Just a sec, getting things ready...'}
        </div>
      </div>
    );
  }

  // Show error if card doesn't have a coin set
  if (!effectiveCoinSymbol) {
    return (
      <div className="purchase-crypto-container ev-page ev-page-enter">
        <div className="loading-card">
          <p>This card does not have a cryptocurrency assigned. <br /> <br /> Please contact support at hello@mycryptocard.io</p>
        </div>
      </div>
    );
  }

  const steps = [
    { id: 1, text: 'Validate your Identity' },
    { id: 2, text: 'Buy Crypto with PayID, card, or Apple/Google Pay', active: true },
    { id: 3, text: 'Store Crypto until ready to sell' },
  ];

  return (
    <div
      className="purchase-crypto-container purchase-crypto--editorial ev-page ev-page-enter"
      data-testid="purchase-crypto-page"
    >
      <div className="purchase-header purchase-header--editorial">
        <h1 className="welcome-display-title">
          <span className="welcome-title-text">Welcome to</span>
          <img src="/logo-my-crypto-card.svg" alt="My Crypto Card" className="welcome-title-logo" />
        </h1>
      </div>

      <div className="welcome-steps welcome-steps--timeline">
        <img src="/steps-banner.png" alt="stepImg" className="steps-banner-img" />
      </div>

      <div className="instruction-text instruction-text--editorial">
        <h2>Own crypto in minutes</h2>
      </div>

      <div className="purchase-main">
        <div className="purchase-hero-card">
        {/* Spend – one row: label + amount + AUD */}
        <div className="card card-row purchase-hero-section">
          <span className="card-label">SPEND</span>
          <div className="spend-card">
            <div className="spend-amount-wrapper">
              <select
                className="spend-amount-select"
                value={amount}
                onChange={handleAmountChange}
                disabled={isLoading}
              >
                <option value="">Select amount</option>
                {amountOptions.map(option => (
                  <option key={option} value={option.toString()}>
                    ${option.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </option>
                ))}
              </select>
            </div>
            <div className="currency-display">
             { /* <div className="currency-flag">🇦🇺</div> */}  
              <span className="currency-code">AUD</span>
            </div>
          </div>
        </div>

        {/* Receive – one row: label + amount + crypto */}
        <div className="card card-row purchase-hero-section">
          <span className="card-label">RECEIVE</span>
          <div className="receive-card">
            <div className="receive-amount">
              {isLoading ? (
                <div className="loading-card">
                  <div className="loading-spinner"></div>
                  <span>Loading...</span>
                </div>
              ) : (
                purchaseCalculation ? purchaseCalculation.cryptoAmount.toFixed(purchaseCalculation.product.decimalPlaces) : '0.00'
              )}
            </div>
            <div className="crypto-display">
              <span className="crypto-name">{coinName}</span>
            </div>
          </div>
        </div>

        {/* Payment Method */}
        {isLoading ? (
          <div className="card card-row card-payment purchase-hero-section">
            <span className="card-label card-label-payment">PAYMENT</span>
            <div className="loading-card">
              <div className="loading-spinner"></div>
              <span>Loading payment options...</span>
            </div>
          </div>
        ) : banxaPrices && banxaPrices.success ? (
          <div className="card card-row card-payment purchase-hero-section">
            <span className="card-label card-label-payment">PAYMENT</span>
            <div className="payment-method-card-row">
              <div className="payment-method-select-wrapper">
                <select
                  className="payment-method-select"
                  data-testid="payment-method-select"
                  value={selectedPaymentMethod?.id ?? ''}
                  onChange={handlePaymentMethodChange}
                  disabled={isLoading || banxaPrices.data.paymentMethods.length === 0}
                  aria-label="Payment method"
                >
                  {banxaPrices.data.paymentMethods.length === 0 ? (
                    <option value="">No payment methods</option>
                  ) : (
                    banxaPrices.data.paymentMethods.map((method) => (
                      <option key={method.id} value={method.id}>
                        {formatPaymentMethodOptionLabel(method)}
                      </option>
                    ))
                  )}
                </select>
              </div>
            </div>
          </div>
        ) : null}

        {/* Summary – compact: Summary + link + Total in minimal rows */}
        <div className="card card-summary purchase-hero-section purchase-hero-section--summary">
          <div className="summary-header">
            <h3 className="summary-title">Summary</h3>
            <div className="show-full-summary" onClick={toggleFullSummary}>
              Show Full summary
              <svg className={`summary-arrow ${showFullSummary ? 'expanded' : ''}`} viewBox="0 0 12 12" fill="currentColor">
                <path d="M6 4L2 8h8L6 4z"/>
              </svg>
            </div>
          </div>
          <div className="total-payable">
            <span className="total-payable-label">Total Payable</span>
            <span className="total-payable-amount">
              {isLoading ? (
                <div className="loading-card">
                  <div className="loading-spinner"></div>
                  <span>Calculating...</span>
                </div>
              ) : (
                <>
                  ${purchaseCalculation ? parseInt(amount).toFixed(2) : '0.00'} AUD
                  <span className="fee-note">(incl. ${purchaseCalculation ? (purchaseCalculation.feeAmount + purchaseCalculation.networkFee).toFixed(2) : '0.00'} fees*)</span>
                </>
              )}
            </span>
          </div>

          {showFullSummary && purchaseCalculation && (
            <div className="full-summary">
              <div className="summary-detail">
                <span className="summary-detail-label">Coin amount</span>
                <span className="summary-detail-value">{purchaseCalculation.cryptoAmount.toFixed(purchaseCalculation.product.decimalPlaces)} {purchaseCalculation.product.symbol}</span>
              </div>
              <div className="summary-detail">
                <span className="summary-detail-label">Crypto spend</span>
                <span className="summary-detail-value">${(purchaseCalculation.amount - purchaseCalculation.feeAmount - purchaseCalculation.networkFee).toFixed(2)} AUD</span>
              </div>
              <div className="summary-detail">
                <span className="summary-detail-label">Processing Fee</span>
                <span className="summary-detail-value">${purchaseCalculation.feeAmount.toFixed(2)} AUD</span>
              </div>
              <div className="summary-detail">
                <span className="summary-detail-label">Estimated network fee</span>
                <span className="summary-detail-value">${purchaseCalculation.networkFee.toFixed(2)} AUD</span>
              </div>
              <div className="summary-detail">
                <span className="summary-detail-label">Total Payable</span>
                <span className="summary-detail-value">${parseInt(amount).toFixed(2)} AUD</span>
              </div>
            </div>
          )}
        </div>
        </div>

        <button
          type="button"
          className="submit-button create-order-btn--pill"
          onClick={handlePurchase}
          disabled={isLoading || !effectiveCoinSymbol || !amount || (!!cardId && !selectedPaymentMethod)}
        >
          Ready? Let&apos;s start the ID Validation
        </button>
      </div>

      {/* Purchase Footer / Learning Hub */}
      <div className="purchase-footer">
        <div className="purchase-footer-text">
          Not ready to make a decision? Want to know more about crypto and My Crypto Card?
        </div>
        <div className="purchase-footer-row">
          <a href="https://mycryptocard.io/learninghub" className="welcome-learning-link" target="_blank" rel="noopener noreferrer">
            Come to our Learning Hub
          </a>
        </div>
      </div>
    </div>
  );
};

export default PurchaseCrypto; 